import type { ContractWorkItemView } from "@closer/shared";
import { Badge, Button, FormField, Input, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * İÇ NOTLAR VE GÖREVLER — organizasyonun KENDİ çalışma yüzeyi (CLOS-1113 · rapor 1 BUG-09).
 *
 * ### ⚠ Neden ayrı bir bölüm: form yanlış ekrandaydı
 *
 * Çalışma kalemi OLUŞTURMA formu "Paylaşım ve Ortak Çalışma" bölümünün içinde duruyordu.
 * Bulgu raporu bunu açıkça kusur sayıyor: *"Bu, paylaşım ekranının amacını
 * bulanıklaştırmaktadır … `Karşı Taraf ve Ortak Çalışma` bölümü yeni private çalışma
 * nesnesi yaratma formu olmamalıdır."*
 *
 * İki soru iki ayrı yüzeye ait:
 *   Bu bölüm      → "kendi ekibimiz ne çalışıyor?"  (private, oluşturma burada)
 *   Paylaşım bölümü → "karşı tarafa ne açtık?"       (yalnız paylaşılanlar + geri çek)
 *
 * ### ⚠ VARSAYILAN PRIVATE — paylaşım AÇIK bir eylem
 *
 * Rapor: *"Private item default olarak karşı tarafa açılmamalıdır. Paylaşım explicit
 * action ile yapılmalıdır."* Kalem burada doğuyor ve private başlıyor; karşı tarafa
 * açmak ayrı bir düğme ve ayrı bir yetki istiyor.
 *
 * ### ⚠ İKİ FARKLI YETKİ, tek ekranda
 *
 * Oluşturma `contract.review` + kayıt yazma istiyor (ucun anahtarı — CLOS-888 dönüşü).
 * Karşı tarafa açmak `contract.share` istiyor, çünkü veriyi başka bir organizasyona
 * taşıyor. Ekran ikisini ayrı ayrı soruyor; tek kapıya indirmek, sunucunun reddedeceği
 * bir düğme göstermek olurdu.
 */
export function ContractWorkItemsSection({
  contractId,
  kayitYazma,
}: {
  contractId: string;
  /** Bu kayıttaki yazma penceresi — yetkiden AYRI bir soru (CLOS-154). */
  kayitYazma: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const anahtar = tenantKey(tenantId, "contract", contractId, "is-kalemleri");

  const [tur, setTur] = useState<"note" | "task">("note");
  const [govde, setGovde] = useState("");

  const kalemler = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<ContractWorkItemView[]>(`/api/contracts/${contractId}/work-items`, { tenantId }))
        .data,
  });

  const ekle = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/work-items`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ itemType: tur, body: govde.trim() }),
      }),
    onSuccess: async () => {
      setGovde("");
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Çalışma kalemi eklendi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Çalışma kalemi eklenemedi"),
  });

  const karsiTarafaAc = useMutation({
    mutationFn: async (itemId: string) =>
      api(`/api/contracts/${contractId}/work-items/${itemId}/share`, { method: "POST", tenantId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Çalışma kalemi karşı tarafa açıldı");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Çalışma kalemi açılamadı"),
  });

  /**
   * GERİ ÇEKME ARTIK BURADA (CLOS-1128): "ne açtık?" listesi mega panelle birlikte kalktı;
   * kalemin görünürlüğü tek yerde, kalemin kendi satırında yönetilir (aç / geri çek).
   */
  const paylasimiGeriCek = useMutation({
    mutationFn: async (itemId: string) =>
      api(`/api/contracts/${contractId}/work-items/${itemId}/unshare`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Çalışma kalemi paylaşımı geri çekildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Paylaşım geri çekilemedi"),
  });
  const olusturabilir = can("contract.review") && kayitYazma;
  const paylasabilir = can("contract.share") && kayitYazma;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-fg-heading">İç Notlar ve Görevler</h2>
        {/*
          ⚠ Bölümün NE OLMADIĞINI söylüyor. Rapor 1'in değişmez ilkesi: kullanıcı
          "buraya yazdığım şeyi karşı taraf görür mü?" sorusunu deneyerek öğrenmemeli.
        */}
        <p className="text-sm text-fg-muted">
          Bu bölüm organizasyonunuzun kendi notlarını ve görevlerini tutar. Buraya eklenen hiçbir
          kayıt, siz açıkça açmadıkça karşı tarafa görünmez.
        </p>
      </div>

      {kalemler.isPending ? (
        <Skeleton lines={2} />
      ) : kalemler.error ? (
        <StateView
          state={resolveErrorState(kalemler.error, {
            idempotent: true,
            overrides: {
              error: { title: "Çalışma kalemleri yüklenemedi", description: "Bölüm getirilemedi." },
            },
          })}
          onRetry={() => void kalemler.refetch()}
        />
      ) : (kalemler.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">Henüz not veya görev eklenmedi.</p>
      ) : (
        <ul className="flex flex-col">
          {(kalemler.data ?? []).map((kalem) => (
            <li
              key={kalem.id}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm text-fg">{kalem.body}</span>
                <span className="text-xs text-fg-soft">
                  {kalem.itemType === "task" ? "Görev" : "Not"} · {kalem.createdByName} ·{" "}
                  {new Date(kalem.createdAt).toLocaleDateString("tr-TR")}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <Badge tone={kalem.visibility === "shared" ? "warning" : "neutral"}>
                  {kalem.visibility === "shared" ? "Karşı tarafa açık" : "Yalnız bizde"}
                </Badge>
                {kalem.visibility === "private" && paylasabilir ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={karsiTarafaAc.isPending}
                    onClick={() => karsiTarafaAc.mutate(kalem.id)}
                  >
                    Karşı tarafa aç
                  </Button>
                ) : kalem.visibility === "shared" && paylasabilir ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={paylasimiGeriCek.isPending}
                    onClick={() => paylasimiGeriCek.mutate(kalem.id)}
                  >
                    Paylaşımı geri çek
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}

      {olusturabilir ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-40">
            <FormField label="Tür">
              <Select
                id="is-kalemi-tur"
                value={tur}
                onChange={(olay) => setTur(olay.target.value as "note" | "task")}
              >
                <option value="note">Not</option>
                <option value="task">Görev</option>
              </Select>
            </FormField>
          </div>
          <div className="min-w-64 flex-1">
            <FormField label="İçerik">
              <Input
                id="is-kalemi-govde"
                value={govde}
                onChange={(olay) => setGovde(olay.target.value)}
                placeholder="Örn. Bedel maddesi için finansmandan görüş al"
              />
            </FormField>
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => ekle.mutate()}
            loading={ekle.isPending}
            disabled={govde.trim() === ""}
          >
            Ekle
          </Button>
        </div>
      ) : null}
    </section>
  );
}
